import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Empty, Input, Modal, Spin, Tooltip, message } from "antd";
import { MergeCellsOutlined, PictureOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { useHistory } from "react-router";
import { getIssueById, mergeIssueInto, searchIssuesForMerge } from "api/issue/issueApi";
import { issueLabel } from "helpers/artifactPrefix";
import { issuePreviewSrc } from "helpers/issuePreview";

// "This issue is a duplicate of ..." - the owner-side way to fold two
// reports of the same problem into one.
//
// The button opens THE PROJECT'S ISSUES, all of them, each row carrying the
// three things somebody picking one actually reads: its picture, its number
// and what it says. It used to open empty and wait for two typed characters,
// which meant choosing the survivor from memory. Now the list is there and
// the search box only narrows it.
//
// Picking one and confirming merges THIS issue into it: people, comments,
// attachments, images and every attribute go onto the survivor (differing
// values are combined, equal ones kept), this issue is abandoned with
// mergedInto stamped, and the browser moves to the survivor.
const T = { muted: "#6B7280", line: "#E5E7EB", ink: "#111827", navy: "#1F3A5F", bg: "#F9FAFB" };

// How many candidates one open pulls down. A project with more issues than
// this keeps the search box working against the server (see `truncated`).
const PAGE = 500;

function statusWord(s) {
  return { open: "Open", pending: "Pending", under_review: "Under review", resolved: "Resolved", abandoned: "Abandoned" }[s] || s || "";
}

// The thumbnail cell. An issue with no media gets a grey placeholder rather
// than a collapsed box, so every row is the same height and the list scans.
function Thumb({ issue }) {
  const [broken, setBroken] = useState(false);
  const src = broken ? "" : issuePreviewSrc(issue);
  return (
    <div style={{ width: 64, height: 48, flex: "0 0 64px", borderRadius: 4, overflow: "hidden",
                  border: `1px solid ${T.line}`, background: "#F3F4F6",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
      {src ? (
        <img src={src} alt="" onError={() => setBroken(true)}
             style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      ) : (
        <PictureOutlined style={{ color: "#C0C6CF", fontSize: 16 }} />
      )}
    </div>
  );
}

// One candidate row: picture, number, title, where/who, description.
function CandidateRow({ issue, selected, onPick }) {
  const loc = [issue.location?.area, issue.location?.level, issue.location?.grid].filter(Boolean).join(" · ");
  const meta = [statusWord(issue.status), issue.priority, loc, issue.assigneeName].filter(Boolean).join(" · ");
  return (
    <div onClick={() => onPick(issue)}
         style={{ padding: "8px 12px", cursor: "pointer", borderBottom: `1px solid ${T.line}`,
                  background: selected ? "#EEF4FF" : "#fff", display: "flex", gap: 10, alignItems: "flex-start" }}>
      <Thumb issue={issue} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
          <span style={{ fontWeight: 700, color: T.navy, fontSize: 12.5, whiteSpace: "nowrap" }}>
            {issueLabel(issue) || `#${issue.displayId}`}
          </span>
          <span style={{ fontSize: 13, color: T.ink, fontWeight: 600, overflow: "hidden",
                         textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {issue.title || "(no title)"}
          </span>
        </div>
        {meta ? <div style={{ fontSize: 11.5, color: T.muted, marginTop: 1 }}>{meta}</div> : null}
        {issue.description ? (
          // Two lines of the description: enough to tell two clashes on the
          // same level apart, not enough to turn the list into a wall.
          <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2, display: "-webkit-box",
                        WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {String(issue.description)}
          </div>
        ) : (
          <div style={{ fontSize: 11.5, color: "#9CA3AF", marginTop: 2, fontStyle: "italic" }}>No description</div>
        )}
      </div>
    </div>
  );
}

export function IssueDuplicateMergeButton({ issue, canMerge, style }) {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);        // the project's candidates
  const [total, setTotal] = useState(0);       // how many the server has
  const [loading, setLoading] = useState(false);
  const [remote, setRemote] = useState(null);  // server search, only when truncated
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const projectId = issue?.projectId?._id || issue?.projectId;
  const finished = ["resolved", "abandoned"].includes(issue?.status);
  const truncated = total > rows.length;

  // The whole list, once, when the picker opens.
  const load = useCallback(async () => {
    if (!projectId) return;
    const mine = ++seq.current;
    setLoading(true);
    try {
      const r = await searchIssuesForMerge(projectId, "", issue?._id, PAGE);
      if (mine !== seq.current) return;
      const list = (r && (r.issues || r.data || (Array.isArray(r) ? r : []))) || [];
      setRows(list.filter((i) => String(i._id) !== String(issue?._id)));
      setTotal(Number(r?.totalCount) || list.length);
    } catch (e) {
      if (mine === seq.current) { setRows([]); setTotal(0); message.error(e?.err || e?.message || "Could not load the project's issues"); }
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [projectId, issue?._id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Only a project bigger than one page needs the server to search: below
  // that the filtering below is instant and exact on what is already here.
  useEffect(() => {
    if (!open || !truncated) { setRemote(null); return undefined; }
    const term = q.trim();
    if (term.length < 2) { setRemote(null); return undefined; }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const r = await searchIssuesForMerge(projectId, term, issue?._id, PAGE);
        if (mine !== seq.current) return;
        const list = (r && (r.issues || r.data || (Array.isArray(r) ? r : []))) || [];
        setRemote(list.filter((i) => String(i._id) !== String(issue?._id)));
      } catch (e) { if (mine === seq.current) setRemote([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open, truncated, projectId, issue?._id]);

  // Narrowing the list: number, title, description, location, people.
  const visible = useMemo(() => {
    const base = remote || rows;
    const term = q.trim().toLowerCase();
    if (!term) return base;
    const num = /^(?:[a-z]{1,8}[-\s]?)?(\d+)$/i.exec(term);
    return base.filter((r) => {
      if (num && String(r.displayId) === num[1]) return true;
      const hay = [
        issueLabel(r), r.displayId, r.title, r.description, r.topic, r.category,
        r.assigneeName, r.contractorLabel, r.status, r.priority,
        r.location?.area, r.location?.level, r.location?.grid,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [rows, remote, q]);

  const reset = () => { setQ(""); setRows([]); setTotal(0); setRemote(null); setPicked(null); setBusy(false); };

  const confirm = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const r = await mergeIssueInto(issue._id, picked._id);
      const target = r?.target || {};
      message.success(`Merged into ${issueLabel({ ...picked, ...target }) || `#${target.displayId}`} - everything is on that issue now`);
      setOpen(false);
      reset();
      // Land on the survivor. Its share token is the page key; fall back
      // to a lookup when the response did not carry it.
      let token = target.shareToken || picked.shareToken;
      if (!token) {
        try { const full = await getIssueById(target._id || picked._id); token = full?.shareToken || full?.issue?.shareToken; } catch (e) { /* stay */ }
      }
      if (token) history.push(`/issue/${token}`);
    } catch (e) {
      message.error(e?.err || e?.message || "Merge failed");
      setBusy(false);
    }
  };

  const pickedLabel = useMemo(() => (picked ? (issueLabel(picked) || `#${picked.displayId}`) : ""), [picked]);

  if (!canMerge || !issue || finished || issue.mergedInto) return null;

  return (
    <>
      <Tooltip title="This issue is a duplicate of another one on this project: find it and merge this one into it. Nothing is lost - people, comments, files, images and attributes all move over.">
        <Button size="small" icon={<MergeCellsOutlined />} onClick={() => { reset(); setOpen(true); }} style={style}>
          Duplicate of…
        </Button>
      </Tooltip>
      <Modal
        open={open}
        title={`Mark ${issueLabel(issue) || `#${issue.displayId}`} as a duplicate`}
        onCancel={() => { if (!busy) { setOpen(false); reset(); } }}
        okText={picked ? `Merge into ${pickedLabel}` : "Pick the original first"}
        okButtonProps={{ disabled: !picked, loading: busy, danger: true }}
        onOk={confirm}
        destroyOnClose
        width={720}
      >
        <p style={{ margin: "0 0 10px", fontSize: 13, color: T.muted }}>
          Every open issue on this project is listed below - picture, number and what it says.
          The one you pick survives; <b>this</b> issue merges into it and is closed as a duplicate.
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <Input
            autoFocus
            allowClear
            prefix={<SearchOutlined style={{ color: T.muted }} />}
            placeholder="Narrow the list - e.g. 126, duct clash, level 2, zone A…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPicked(null); }}
          />
          <Tooltip title="Reload the project's issues">
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
          </Tooltip>
        </div>
        <div style={{ marginTop: 6, fontSize: 11.5, color: T.muted }}>
          {loading ? "Loading the project's issues…"
            : truncated
              ? `Showing ${visible.length} of ${total} - type to search the rest`
              : `${visible.length}${q.trim() ? ` of ${rows.length}` : ""} issue${visible.length === 1 ? "" : "s"} you can merge into`}
        </div>
        <div style={{ marginTop: 6, maxHeight: 380, overflow: "auto", border: `1px solid ${T.line}`, borderRadius: 6 }}>
          {loading && !rows.length ? (
            <div style={{ padding: 28, textAlign: "center" }}><Spin /></div>
          ) : !visible.length ? (
            <div style={{ padding: 18 }}>
              <Empty imageStyle={{ height: 48 }}
                     description={<span style={{ fontSize: 12.5, color: T.muted }}>
                       {q.trim() ? "No issue on this project matches that." : "This project has no other open issue to merge into."}
                     </span>} />
            </div>
          ) : visible.map((r) => (
            <CandidateRow key={r._id} issue={r} selected={picked && String(picked._id) === String(r._id)} onPick={setPicked} />
          ))}
        </div>
        {picked && (
          <div style={{ marginTop: 10, fontSize: 12.5, background: T.bg, border: `1px solid ${T.line}`, borderRadius: 6, padding: "8px 10px" }}>
            <b>{issueLabel(issue) || `#${issue.displayId}`}</b> will merge into <b>{pickedLabel}</b>: responsible people, comments,
            attachments, images, sheets and attributes move over (different values are combined, equal ones kept once).
            This issue is then closed as a duplicate and stays readable.
          </div>
        )}
      </Modal>
    </>
  );
}

// Shown on an issue that has already been merged away.
export function IssueMergedBanner({ issue }) {
  const history = useHistory();
  const [target, setTarget] = useState(null);
  const id = issue?.mergedInto && (issue.mergedInto._id || issue.mergedInto);
  useEffect(() => {
    let alive = true;
    if (!id) { setTarget(null); return undefined; }
    if (issue.mergedInto && issue.mergedInto.shareToken) { setTarget(issue.mergedInto); return undefined; }
    getIssueById(id).then((r) => { if (alive) setTarget(r?.issue || r || null); }).catch(() => { if (alive) setTarget(null); });
    return () => { alive = false; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!id) return null;
  const label = target ? (issueLabel(target) || `#${target.displayId}`) : "another issue";
  return (
    <div style={{ background: "#FFF7ED", border: "1px solid #FDBA74", color: "#9A3412", borderRadius: 6, padding: "6px 10px", fontSize: 12.5 }}>
      This issue was merged into <b>{label}</b> as a duplicate; everything it held lives there now.
      {target?.shareToken && (
        <Button size="small" type="link" style={{ paddingLeft: 6 }} onClick={() => history.push(`/issue/${target.shareToken}`)}>
          Open {label}
        </Button>
      )}
    </div>
  );
}

export default IssueDuplicateMergeButton;
